/**
 * Deterministic classifiers — decided by rule only, no AI (Core PRD Invariant I2).
 * Faithful port of the prototype's classifyMedical / classifyAutomotive. Pure functions:
 * the same facts always yield the same result, and unmatched facts abstain rather than
 * guess. Abstention is a designed state, not an error.
 */
import type { AutomotiveInput, ClassifyResult, MedicalInput } from './types.js';

export function classifyMedical(inp: MedicalInput): ClassifyResult {
  const p = inp.purpose;
  if (inp.nanomaterial) {
    return {
      ok: false,
      abstain: 'No rule applies',
      reason:
        'Devices made with nanomaterials fall under Annex VIII Rule 19, which is not yet held. No rule is stretched to fit — the system abstains.',
    };
  }
  if (inp.active && (p.includes('administer_medicine') || p.includes('remove_medicine'))) {
    const hz = inp.hazardous !== false;
    return { ok: true, class: hz ? 'Class IIb' : 'Class IIa', rule: 'EU-MDR-AnnexVIII-R12', cite: 'Regulation (EU) 2017/745 — Annex VIII, Rule 12' };
  }
  if (inp.implantable && inp.contact === 'heart_cns') {
    return { ok: true, class: 'Class III', rule: 'EU-MDR-AnnexVIII-R8', cite: 'Regulation (EU) 2017/745 — Annex VIII, Rule 8' };
  }
  if (inp.implantable || (inp.invasive === 'surgical' && inp.duration === 'long_term')) {
    return { ok: true, class: 'Class IIb', rule: 'EU-MDR-AnnexVIII-R8', cite: 'Regulation (EU) 2017/745 — Annex VIII, Rule 8' };
  }
  if (inp.contact === 'teeth') {
    return { ok: true, class: 'Class IIa', rule: 'EU-MDR-AnnexVIII-R8', cite: 'Regulation (EU) 2017/745 — Annex VIII, Rule 8' };
  }
  if (inp.invasive === 'surgical' && inp.duration === 'transient') {
    return { ok: true, class: 'Class IIa', rule: 'EU-MDR-AnnexVIII-R6', cite: 'Regulation (EU) 2017/745 — Annex VIII, Rule 6' };
  }
  if (inp.active && p.includes('diagnosis')) {
    return { ok: true, class: 'Class IIa', rule: 'EU-MDR-AnnexVIII-R11', cite: 'Regulation (EU) 2017/745 — Annex VIII, Rule 11' };
  }
  if (inp.invasive === 'none' && !inp.active) {
    return { ok: true, class: 'Class I', rule: 'EU-MDR-AnnexVIII-R1', cite: 'Regulation (EU) 2017/745 — Annex VIII, Rule 1' };
  }
  return {
    ok: false,
    abstain: 'No rule applies',
    reason: 'These facts match no rule in the corpus. The system abstains rather than guess a class.',
  };
}

export function classifyAutomotive(inp: AutomotiveInput): ClassifyResult {
  if (inp.use === 'passenger') {
    if (inp.seats <= 8) return { ok: true, class: 'Passenger car (M1)', rule: 'UNECE-R13H-Brake-Class', cite: 'UN Regulation No. 13-H (braking)' };
    if (inp.massKg <= 5000) {
      return {
        ok: true, class: 'Minibus (M2)', rule: 'UNECE-R107-M2M3', cite: 'UN Regulation No. 107', soft: true,
        abstainNote: 'The vehicle type is a minibus (M2). The braking rule for it (UN R13) is not yet held, so the braking requirement abstains.',
      };
    }
    return { ok: true, class: 'Bus / coach (M3)', rule: 'UNECE-R107-M2M3', cite: 'UN Regulation No. 107', soft: true, abstainNote: 'The braking requirement abstains — UN R13 is not yet held.' };
  }
  if (inp.use === 'goods') {
    if (inp.massKg <= 3500) return { ok: true, class: 'Light goods (N1)', rule: 'UNECE-R13H-Brake-Class', cite: 'UN Regulation No. 13-H (braking)' };
    return {
      ok: false, abstain: 'No rule applies',
      reason: 'A goods vehicle over 3,500 kg is type N2 or N3. The braking rule (UN R13) is not yet held — coverage is thin, so the system abstains.',
    };
  }
  return { ok: false, abstain: 'No rule applies', reason: 'The vehicle’s use has not been stated.' };
}
